// translations/pages/kyveil/de.js

export const kyveilPageDE = {
  "seo": {
    "title": "Kyveil PII-Anonymisierung | pAIoneer Labs GmbH",
    "description": "Kyveil erkennt und anonymisiert personenbezogene Daten lokal auf Ihren Systemen. Desktop-Anwendung oder REST-API für DSGVO-konforme PII-Anonymisierung.",
    "keywords": "Kyveil, PII Anonymisierung, personenbezogene Daten anonymisieren, Datenschutz, DSGVO, GDPR, Microsoft Presidio, spaCy, flair, lokale Datenverarbeitung",
    "ogTitle": "Kyveil: Professionelle PII-Anonymisierung mit KI und NLP",
    "ogDescription": "Automatische Erkennung und Anonymisierung personenbezogener Daten als Desktop-Anwendung oder REST-API auf Ihrer eigenen Infrastruktur.",
    "twitterTitle": "Kyveil PII-Anonymisierung",
    "twitterDescription": "Lokale PII-Erkennung und Anonymisierung für sensible Dokumente.",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "Software zur lokalen Erkennung und Anonymisierung personenbezogener Daten mit KI und NLP.",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "Datenschutz und Anonymisierung",
    "title": "Kyveil",
    "subtitle": "Lokale Anonymisierung vor KI, Tests und Weitergabe.",
    "description": "Kyveil ist eine lokale Anonymisierungsschicht für Dokumente und KI-Workflows. Die Software erkennt personenbezogene Daten in Word-, Excel-, PDF- und Textdateien, ersetzt oder maskiert sie und zeigt eine Vorschau, bevor Inhalte in KI-Chats, Tests, Analysen oder externe Weitergabe gehen. Entwickelt und kontinuierlich weiterentwickelt wird Kyveil [IDD-getrieben](idd) (Intent-Driven Development) und mit [Veridion](veridion) automatisiert durch unsere KI-Mitarbeiter mit Menschen im Loop.",
    "platforms": "Desktop-Anwendung, REST-API und AnonChat",
    "proofPoints": [
      "Dokumente bleiben lokal auf Ihren Systemen.",
      "Personenbezogene Daten werden ersetzt oder maskiert.",
      "Eine Vorschau zeigt den anonymisierten Inhalt vor Weitergabe oder KI-Chat.",
      "Word, Excel, PDF und Text sind die zentralen Dokument-Workflows."
    ]
  },
  "dataSovereignty": {
    "title": "Ihre Daten. Ihre Systeme. Ihre Kontrolle.",
    "subtitle": "Vollständige Datensouveränität durch lokale Verarbeitung.",
    "items": [
      {
        "title": "Keine Cloud erforderlich",
        "description": "Die gesamte Verarbeitung erfolgt lokal auf Ihrer Infrastruktur. Ihre Dateien verlassen niemals Ihre Systeme. Keine Cloud-Abhängigkeiten, keine externe Datenübertragung."
      },
      {
        "title": "Edge Computing",
        "description": "Die Desktop-Anwendung läuft vollständig eigenständig auf Arbeitsplatzrechnern. Die API wird auf Ihren eigenen Servern betrieben. Beide Varianten verarbeiten alle Daten lokal innerhalb Ihrer Sicherheitszone."
      },
      {
        "title": "Datenautonomie",
        "description": "Vollständige Kontrolle über Ihre Datenflüsse. Keine Vendor-Lock-in-Effekte. Keine laufenden Cloud-Kosten. Ihre sensiblen Informationen bleiben ausschließlich unter Ihrer Kontrolle."
      },
      {
        "title": "Standard-Hardware",
        "description": "Keine GPU erforderlich. Läuft auf handelsüblichen Office-Computern und Standard-Servern. Keine Investitionen in spezialisierte Hardware notwendig."
      }
    ]
  },
  "features": {
    "title": "Kernfunktionen",
    "subtitle": "Alles, was für kontrollierte PII-Erkennung und Anonymisierung in Unternehmensprozessen wichtig ist.",
    "items": [
      {
        "title": "KI-basierte PII-Erkennung",
        "description": "Automatische Erkennung mit Microsoft Presidio SDK, spaCy von Explosion AI und flair von der Humboldt-Universität Berlin.",
        "tone": "primary"
      },
      {
        "title": "Replace-Modus",
        "description": "Ersetzt erkannte PII durch generische Platzhalter wie [NAME], [EMAIL] und [ADRESSE] für Compliance-Szenarien.",
        "tone": "info"
      },
      {
        "title": "Fake-Modus",
        "description": "Ersetzt erkannte PII durch plausible Ersatzwerte für Testumgebungen und Entwicklung.",
        "tone": "warning"
      },
      {
        "title": "Mehrere Dokumentformate",
        "description": "Verarbeitet Microsoft Word, Excel, OpenDocument, PDF-Dateien und Plain Text.",
        "tone": "success"
      },
      {
        "title": "Mehrsprachige Unterstützung",
        "description": "Integrierte Unterstützung für Deutsch, Englisch, Französisch, Italienisch und Spanisch mit sprachspezifischen NLP-Modellen.",
        "tone": "secondary"
      },
      {
        "title": "Konfigurierbare Erkennung",
        "description": "Definieren Sie, welche PII-Entitäten erkannt werden: Namen, Adressen, E-Mails, Telefonnummern, Bankkonten, Steuernummern und mehr.",
        "tone": "primary"
      },
      {
        "title": "Erweiterbarkeit",
        "description": "Schnittstellen für eigene Pre- und Post-Processing-Logik zur Integration in Ihre spezifischen Workflows.",
        "tone": "info"
      },
      {
        "title": "CPU-optimiert",
        "description": "Effiziente Modellarchitektur läuft auf Standard-Office-Hardware ohne GPU-Anforderungen.",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "Technologie-Stack",
    "subtitle": "Enterprise-Frameworks und bewährte NLP-Forschung.",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK. Industrie-Standard-Framework für PII-Erkennung und Anonymisierung mit integrierten Entity-Recognizern.",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Entwickelt von Explosion AI",
        "description": "Industrial-Strength Natural Language Processing. Produktionsreife NLP-Bibliothek mit vortrainierten statistischen Modellen für Named Entity Recognition.",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "Entwickelt von der Humboldt-Universität zu Berlin",
        "description": "State-of-the-Art NLP-Framework mit kontextuellen String-Embeddings. Fortgeschrittene neuronale Netzwerkmodelle für verbesserte Entity-Detection-Genauigkeit.",
        "tone": "secondary"
      }
    ],
    "integration": "Integrierte KI-Pipeline",
    "integrationDesc": "Kombinierte Erkennung mit Presidio als Orchestrierungsschicht und spaCy- sowie flair-Modellen für optimale Detection über mehrere Sprachen und Entity-Typen hinweg."
  },
  "deployment": {
    "title": "Deployment-Optionen",
    "subtitle": "Wählen Sie die passende Lösung für Ihren Anwendungsfall.",
    "options": [
      {
        "title": "Desktop-Anwendung",
        "subtitle": "Standalone-Anwendung für einzelne Arbeitsplätze",
        "description": "Die Desktop-Version läuft vollständig eigenständig auf Windows, macOS oder Linux. Ideal für Anwender, die Dokumente direkt am Arbeitsplatz ohne Netzwerkabhängigkeiten anonymisieren müssen.",
        "features": [
          "100% Offline-Fähigkeit - keine Internetverbindung erforderlich",
          "Dateien verlassen niemals den lokalen Arbeitsplatzrechner",
          "Batch-Verarbeitung für mehrere Dateien",
          "Grafische Benutzeroberfläche für einfache Bedienung",
          "Läuft auf Standard-Office-Computern ohne GPU",
          "Einmalige Lizenz - kein Abonnement-Modell"
        ]
      },
      {
        "title": "REST-API",
        "subtitle": "Server-Deployment für Integration und Skalierung",
        "description": "Die API-Version wird auf Ihren eigenen Servern oder Private-Cloud-Infrastruktur betrieben. Ideal für die Integration von Anonymisierung in bestehende Systeme und Workflows mit hohem Verarbeitungsvolumen.",
        "features": [
          "Deployment auf eigener Infrastruktur - On-Premise oder Private Cloud",
          "Horizontale Skalierung für hohe Durchsatzanforderungen",
          "RESTful-API mit umfassender Dokumentation",
          "Identische Anonymisierungs-Engine wie Desktop-Version",
          "Integrationsbeispiele für gängige Plattformen",
          "Unterstützung für Pre- und Post-Processing-Anpassungen"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "KI-Webchat mit vorgeschalteter Anonymisierung",
        "description": "AnonChat stellt Kyveil vor den eigentlichen KI-Chat. Word- und Excel-Dokumente werden anonymisiert, eine Vorschau wird geprüft und erst danach gehen Inhalte in ChatGPT, Claude, Gemini oder interne LLMs.",
        "features": [
          "Anonymisierung vor dem Chat statt nachträglicher Kontrolle",
          "Vorschau des anonymisierten Textes vor der Übergabe",
          "Geeignet für dokumentenbasierte Fragen an KI-Systeme",
          "Reduziert das Risiko, personenbezogene Daten an externe Modelle zu senden",
          "Kann an interne LLM- oder Enterprise-Chat-Setups angebunden werden",
          "Basiert auf dem gleichen Anonymisierungsansatz wie Desktop und REST-API"
        ]
      }
    ],
    "bothNote": "Desktop-Anwendung, REST-API und AnonChat sind darauf ausgelegt, sensible Inhalte vor der Weiterverarbeitung kontrolliert zu anonymisieren."
  },
  "technicalDetails": {
    "title": "Technische Fähigkeiten",
    "subtitle": "Konfigurierbare Erkennung für europäische Geschäftsdokumente.",
    "items": [
      {
        "title": "Sprachunterstützung",
        "description": "Vortrainierte NLP-Modelle für Deutsch, Englisch, Französisch, Italienisch und Spanisch. Sprachspezifische Entity-Recognition für präzise PII-Erkennung in europäischen Geschäftskontexten.",
        "tone": "primary"
      },
      {
        "title": "Konfigurierbare PII-Entitäten",
        "description": "Wählen Sie aus, welche personenbezogenen Daten erkannt und anonymisiert werden: Personennamen, E-Mail-Adressen, Telefonnummern, Postadressen, IBAN-Bankkonten, Kreditkartennummern, Sozialversicherungsnummern, Steuernummern, Reisepassnummern und mehr.",
        "tone": "info"
      },
      {
        "title": "Anonymisierungs-Modi",
        "description": "Replace-Modus ersetzt PII durch generische Platzhalter für Compliance-Dokumentation. Fake-Modus nutzt plausible Ersatzwerte für Testumgebungen unter Beibehaltung der Dokumentstruktur.",
        "tone": "warning"
      },
      {
        "title": "Pre- und Post-Processing",
        "description": "Implementieren Sie eigene Logik vor und nach der Anonymisierung. Fügen Sie Dokumentvalidierung, Formatkonvertierung, Qualitätsprüfungen, Audit-Logging oder Integration mit nachgelagerten Systemen über bereitgestellte Schnittstellen hinzu.",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChat erweitert Kyveil um einen KI-Webchat",
    "subtitle": "Die neue App nutzt den gleichen Anonymisierungsansatz vor dem eigentlichen Chat mit externen oder internen KI-Systemen.",
    "eyebrow": "Neue Anwendung",
    "name": "AnonChat",
    "description": "AnonChat ist ein KI-Webchat, bei dem Word- und Excel-Dokumente vor der Nutzung mit ChatGPT, Claude, Gemini oder internen LLMs anonymisiert werden können. Eine Vorschau zeigt den anonymisierten Text, bevor Inhalte in den Chat übernommen werden.",
    "statement": "Damit liegt die Entscheidung beim Nutzer: prüfen, freigeben, dann erst chatten.",
    "steps": [
      {
        "title": "Dokument laden",
        "description": "Word- oder Excel-Dateien werden für die Anonymisierung vorbereitet, bevor sie in den KI-Workflow gehen.",
        "tone": "primary"
      },
      {
        "title": "PII anonymisieren",
        "description": "Kyveil erkennt personenbezogene Daten und ersetzt oder maskiert sie nach den gewählten Regeln.",
        "tone": "info"
      },
      {
        "title": "Vorschau prüfen",
        "description": "Der anonymisierte Text wird sichtbar, bevor Inhalte an den Chat übergeben werden.",
        "tone": "warning"
      },
      {
        "title": "KI-Chat nutzen",
        "description": "Erst nach der Prüfung werden die anonymisierten Inhalte für den Chat mit externen oder internen LLMs verwendet.",
        "tone": "success"
      }
    ]
  },
  "useCases": {
    "title": "Anwendungsfälle",
    "subtitle": "Von DSGVO-Konformität bis sicheren Entwicklungsdaten.",
    "items": [
      {
        "title": "DSGVO-Konformität",
        "description": "Anonymisieren Sie personenbezogene Daten vor der externen Weitergabe oder Verwendung in internen Abteilungen. Erfüllen Sie Datenschutzanforderungen für Testdaten, Analyse-Datensätze und Dokumentation.",
        "tone": "primary"
      },
      {
        "title": "Testdaten-Aufbereitung",
        "description": "Bereiten Sie realistische Test-Datensätze so auf, dass Entwickler produktionsnahe Daten nutzen können, ohne echte persönliche Informationen preiszugeben.",
        "tone": "info"
      },
      {
        "title": "HR-Dokumentenverarbeitung",
        "description": "Anonymisieren Sie Bewerbungen, Personalakten und HR-Dokumentation für unvoreingenommene Prüfungen, Compliance-Audits oder Datenanalysen unter Wahrung der Privatsphäre.",
        "tone": "warning"
      },
      {
        "title": "Datenanalyse",
        "description": "Ermöglichen Sie die Analyse von Dokumenten mit PII durch vorherige Anonymisierung sensibler Informationen. Teilen Sie Datensätze teamübergreifend ohne Datenschutzbedenken.",
        "tone": "success"
      },
      {
        "title": "Dokumentenarchivierung",
        "description": "Bereiten Sie Dokumente für die Langzeitarchivierung vor, indem PII gemäß Aufbewahrungsrichtlinien und Datenschutzvorschriften entfernt oder anonymisiert wird.",
        "tone": "secondary"
      },
      {
        "title": "Sichere Weitergabe",
        "description": "Geben Sie Dokumente an externe Partner, Auftragnehmer oder andere Abteilungen weiter, nachdem sensible persönliche Informationen automatisch entfernt wurden.",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "Varianten und Support",
    "subtitle": "Updates, Erweiterungen und Unterstützung für produktive Nutzung.",
    "items": [
      {
        "title": "Desktop, API und AnonChat",
        "description": "Kyveil gibt es als Desktop-Anwendung und als REST-API für Integrationen. AnonChat ist der Webchat mit vorgeschalteter Dokument-Anonymisierung.",
        "tone": "info"
      },
      {
        "title": "Support",
        "description": "Für den Einsatz im Unternehmen bieten wir technischen Support, Hilfe bei der Implementierung und Anpassungen an.",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Starten Sie mit Kyveil",
    "subtitle": "Schützen Sie sensible Dokumente, bevor sie in KI, Tests, Analysen oder Weitergabe gelangen.",
    "desktop": "Desktop-Anwendung anfragen",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "Kontakt für REST-API Deployment",
    "contact": "Für eine Demo oder ein Angebot schreiben Sie uns.",
    "email": "info@paioneer.eu",
    "apiSubject": "Kyveil REST API Deployment",
    "contactSubject": "Kyveil Demo oder Angebot"
  }
};
